import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { AuthDialogProvider } from "@/components/AuthDialog";
import { Toaster } from "@/components/ui/sonner";
import PushAlertsPrompt from "@/components/PushAlertsPrompt";
import Home from "@/pages/Home";
import useAnalytics from "@/hooks/useAnalytics";

// Secondary routes are code-split so the initial JS bundle stays small.
const History = React.lazy(() => import("@/pages/History"));
const PlanLanding = React.lazy(() => import("@/pages/PlanLanding"));
const NicaraguaTaxi = React.lazy(() => import("@/pages/NicaraguaTaxi"));
const SJDSLocals = React.lazy(() => import("@/pages/SJDSLocals"));
const BeachDetail = React.lazy(() => import("@/pages/BeachDetail"));
const BeachesIndex = React.lazy(() => import("@/pages/BeachesIndex"));
const BlogIndex = React.lazy(() => import("@/pages/BlogIndex"));
const BlogPost = React.lazy(() => import("@/pages/BlogPost"));

function RouteFallback() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center text-[color:var(--ink-2)]" data-testid="route-loading">
      <div className="w-6 h-6 border-2 border-[color:var(--coral)] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * Reset scroll to the top whenever the user navigates to a new route.
 * Honors in-page hash anchors (e.g. /history#playa-maderas) so smooth-scroll
 * still works there. Uses useLayoutEffect so the jump happens BEFORE the new
 * page paints, eliminating the flash of "middle of the page".
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useAnalytics();
  React.useLayoutEffect(() => {
    if (hash) return; // let the browser handle anchor jumps
    // NOTE: this app's real scroll container is `#root`, not `window` (see
    // `index.css`: `html, body, #root { height: 100%; overflow-x: hidden }`
    // — the `#root` div becomes the y-scroller). So we MUST reset #root too,
    // otherwise SPA navigations arrive mid-page.
    const jump = () => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch (_e) {
        window.scrollTo(0, 0);
      }
      const root = document.getElementById("root");
      if (root) root.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    jump();
    requestAnimationFrame(jump);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  // Stop the browser from restoring previous scroll positions across page
  // changes — we always want fresh navigations to start at the top.
  React.useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);
  return (
    <I18nProvider>
      <AuthProvider>
        <AuthDialogProvider>
          <div className="App">
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/nicaragua-taxi" element={<NicaraguaTaxi />} />
                  <Route path="/sjds-locals" element={<SJDSLocals />} />
                  <Route path="/surf/:slug" element={<BeachDetail />} />
                  <Route path="/beaches" element={<BeachesIndex />} />
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/plan/:id" element={<PlanLanding />} />
                  <Route path="*" element={<Home />} />
                </Routes>
              </Suspense>
              <PushAlertsPrompt />
            </BrowserRouter>
            <Toaster />
          </div>
        </AuthDialogProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
